import { useNavigate } from "react-router-dom";
import useScrollDetection from "../../hooks/useScrollDetection";
import { IoArrowBack, IoInformationCircleOutline } from "react-icons/io5";
import { GoPencil } from "react-icons/go";
import { useFormik } from "formik";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import DatePickerInput from "../../components/common/DatePickerInput";
import { useContext, useState } from "react";
import { GlobalStateContext } from "../../components/context/GlobalStateContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getDetailUserProfile,
  removeImageProfile,
  updateUserProfile,
  uploadImageProfile,
} from "../../services/profileApi";
import * as yup from "yup";
import { FaUserCircle } from "react-icons/fa";
import { useNotification } from "../../components/context/NotificationContext";
import { IoTrashOutline } from "react-icons/io5";
import HOC from "../../components/HOC";
import { useDispatch } from "react-redux";
import { syncProfileToStorage } from "../../redux/features/auth/authSlice";

const textFieldStyles = {
  backgroundColor: "white",
  borderRadius: "10px",
  color: "#111827",
  transition: "all 0.4s",
  fontFamily: '"Montserrat", sans-serif',
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#d1d5db",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#9ca3af",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#111827",
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    padding: "14px 12px",
    color: "#111827",
    fontSize: { xs: "14px", lg: "16px" },
    fontFamily: '"Montserrat", sans-serif',
    "&::placeholder": {
      color: "#CBCBCB",
      opacity: 1,
      fontFamily: '"Montserrat", sans-serif',
    },
  },
  "&.Mui-disabled": {
    backgroundColor: "#f9fafb",
    opacity: 0.7,
  },
};

const errorTextFieldStyles = {
  ...textFieldStyles,
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
    borderColor: "#ef4444",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
  },
};

const formHelperFieldStyles = {
  width: "100%",
  display: "flex",
  justifyContent: "start",
  margin: "2px 0 0 0",
  fontSize: "12px",
  color: "#ef4444",
  fontFamily: '"Montserrat", sans-serif',
};

const getCleanPhoneNumber = (formatted) => {
  return formatted.replace(/[^\d+]/g, "");
};

const validationSchema = yup.object({
  fullname: yup.string().required("Nama lengkap wajib diisi"),
  email: yup
    .string()
    .email("Format email tidak valid")
    .required("Email wajib diisi"),
  nik: yup
    .string()
    .required("NIK wajib diisi")
    .matches(/^\d{16}$/, "NIK harus terdiri dari 16 digit angka"),
  phone_number: yup
    .string()
    .required("Nomor ponsel wajib diisi")
    .test(
      "phone-validation",
      "Format nomor ponsel tidak valid (min. 10 digit)",
      function (value) {
        if (!value) return false;
        const cleanPhone = getCleanPhoneNumber(value);
        const phoneRegex = /^\+628\d{9,13}$/;
        return phoneRegex.test(cleanPhone);
      },
    ),
  date_of_birth: yup.string().required("Tanggal lahir wajib diisi"),
  gender: yup
    .number()
    .min(1, "Jenis kelamin wajib dipilih")
    .required("Jenis kelamin wajib dipilih"),
  weight: yup.number(),
  height: yup.number(),
  action_type: yup.number(),
});

const ChangeProfile = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isScrolled = useScrollDetection(5);
  const { isLogin } = useContext(GlobalStateContext);
  const { showNotification } = useNotification();

  const [errorMessage, setErrorMessage] = useState("");
  const [imageErr, setImageErr] = useState("");

  const getFieldStyles = (hasError) => {
    return hasError ? errorTextFieldStyles : textFieldStyles;
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/[^\d+]/g, "");

    if (cleaned.startsWith("+62")) {
      const numbers = cleaned.substring(3);
      const limitedNumbers = numbers.substring(0, 13);

      return "(+62) " + limitedNumbers;
    }

    return value;
  };

  const handlePhoneChange = (value) => {
    let cleaned = value.replace(/[^\d]/g, "");

    if (cleaned.startsWith("0")) {
      cleaned = "62" + cleaned.substring(1);
    } else if (cleaned.startsWith("8")) {
      cleaned = "62" + cleaned;
    } else if (!cleaned.startsWith("62")) {
      cleaned = "62" + cleaned;
    }

    cleaned = cleaned.substring(0, 15);

    const formatted = formatPhoneNumber("+" + cleaned);
    formik.setFieldValue("phone_number", formatted);
  };

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["detail-user-profile"],
    queryFn: () => getDetailUserProfile(),
    enabled: isLogin === true,
    keepPreviousData: false,
  });

  const { mutate: mutateUpdate, isPending } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (_, variables) => {
      showNotification("Berhasil memperbaharui profil", "success");
      dispatch(
        syncProfileToStorage({
          fullname: variables?.fullname,
          email: variables?.email,
          profile_image: variables?.profile_image,
          phone_number: variables?.phone_number,
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["detail-user-profile"],
      });
      navigate("/profile");
    },
    onError: (err) => {
      setErrorMessage(err.message.id || "Terjadi kesalahan saat mendaftar");
    },
  });

  const { mutate: mutateUploadImage } = useMutation({
    mutationFn: uploadImageProfile,
    onSuccess: (data) => {
      formik.setFieldValue("profile_image", data.image_url);
    },
    onMutate: () => setImageErr(""),
    onError: (err) =>
      setImageErr(err.message.id ? err.message.id : err.message),
  });

  const { mutate: mutateDeleteImage } = useMutation({
    mutationFn: removeImageProfile,
    onSuccess: () => formik.setFieldValue("profile_image", ""),
    onMutate: () => setImageErr(""),
    onError: (err) =>
      setImageErr(err.message.id ? err.message.id : err.message),
  });

  const formik = useFormik({
    initialValues: {
      profile_image: data ? data.profile_image : "",
      fullname: data ? data.fullname : "",
      nik: data && data.nik === "0" ? "" : data.nik,
      email: data ? data.email : "",
      phone_number: data ? data.phone_number : "",
      date_of_birth: data ? data.date_of_birth : "",
      gender: data ? data.gender : 0,
      weight: data ? data.weight : 0,
      height: data ? data.height : 0,
      action_type: 1,
    },
    validationSchema,
    onSubmit: (value) => {
      const submitData = {
        profile_image: value.profile_image,
        fullname: value.fullname,
        nik: value.nik,
        email: value.email,
        phone_number: getCleanPhoneNumber(value.phone_number),
        date_of_birth: value.date_of_birth,
        gender: value.gender,
        weight: value.weight,
        height: value.height,
        action_type: value.action_type,
      };
      mutateUpdate(submitData);
    },
    validateOnMount: true,
  });

  return (
    <div className="bg-white relative">
      <div className="flex flex-row justify-center min-h-screen">
        <div className="w-full lg:w-1/2 flex justify-center lg:items-center px-0 lg:px-12 text-base">
          <div className="rounded-md p-3 lg:p-8 w-full h-fit">
            <div className="w-full">
              <div className="mb-6 w-full flex justify-center">
                <div className="relative">
                  {formik.values.profile_image === "" ? (
                    <label htmlFor="dropzone-file1" className="cursor-pointer">
                      <div className="relative group">
                        <FaUserCircle className="w-[150px] h-[150px] text-gray-300" />
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <GoPencil size={32} className="text-white" />
                        </div>
                      </div>
                      <input
                        id="dropzone-file1"
                        type="file"
                        accept="image/jpeg, image/jpg, image/png"
                        onChange={(e) => mutateUploadImage(e.target.files[0])}
                        hidden
                      />
                    </label>
                  ) : (
                    <>
                      <label
                        htmlFor="dropzone-file1"
                        className="cursor-pointer block relative group"
                      >
                        <img
                          src={formik.values.profile_image}
                          alt="Profile IMG"
                          className="w-[150px] h-[150px] rounded-full object-cover border-2 border-gray-200"
                        />
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <GoPencil size={32} className="text-white" />
                        </div>
                        <input
                          id="dropzone-file1"
                          type="file"
                          accept="image/jpeg, image/jpg, image/png"
                          onChange={(e) => mutateUploadImage(e.target.files[0])}
                          hidden
                        />
                      </label>

                      <div
                        onClick={() =>
                          mutateDeleteImage(formik.values.profile_image)
                        }
                        className="p-2 bg-white border shadow-md border-gray-300 rounded-full absolute bottom-0 right-0 cursor-pointer hover:bg-red-50 transition-colors duration-200"
                      >
                        <IoTrashOutline size={20} className="text-error" />
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-center text-error">{imageErr}</p>

              <form onSubmit={formik.handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <p>Nama Lengkap</p>
                    <TextField
                      fullWidth
                      placeholder="Masukkan nama lengkap"
                      value={formik.values.fullname}
                      onChange={(e) =>
                        formik.setFieldValue("fullname", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      name="fullname"
                      helperText={
                        formik.touched.fullname && formik.errors.fullname
                      }
                      error={
                        formik.touched.fullname &&
                        Boolean(formik.errors.fullname)
                      }
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.fullname && formik.touched.fullname,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: formHelperFieldStyles,
                      }}
                    />
                  </div>

                  <div>
                    <p>Email</p>
                    <TextField
                      fullWidth
                      placeholder="Masukkan email"
                      value={formik.values.email}
                      onChange={(e) =>
                        formik.setFieldValue("email", e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      name="email"
                      helperText={formik.touched.email && formik.errors.email}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.email && formik.touched.email,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: formHelperFieldStyles,
                      }}
                    />
                  </div>

                  <div>
                    <p>NIK</p>
                    <TextField
                      fullWidth
                      placeholder="Masukkan 16 digit NIK"
                      value={formik.values.nik}
                      onChange={(e) => {
                        const val = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 16);
                        formik.setFieldValue("nik", val);
                      }}
                      onBlur={formik.handleBlur}
                      name="nik"
                      inputProps={{ inputMode: "numeric", maxLength: 16 }}
                      helperText={
                        formik.touched.nik && formik.errors.nik
                          ? formik.errors.nik
                          : formik.values.nik?.length > 0
                            ? `${formik.values.nik?.length}/16 digit`
                            : ""
                      }
                      error={formik.touched.nik && Boolean(formik.errors.nik)}
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.nik && formik.touched.nik,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: {
                          ...formHelperFieldStyles,
                          color:
                            formik.touched.nik && formik.errors.nik
                              ? "#ef4444"
                              : "#9ca3af",
                        },
                      }}
                    />
                  </div>

                  <div>
                    <p>Nomor Ponsel</p>
                    <TextField
                      fullWidth
                      placeholder="(+62) 8xxxxxxxxxx"
                      value={formik.values.phone_number}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      onBlur={formik.handleBlur}
                      name="phone_number"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      helperText={
                        formik.touched.phone_number &&
                        formik.errors.phone_number
                      }
                      error={
                        formik.touched.phone_number &&
                        Boolean(formik.errors.phone_number)
                      }
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.phone_number &&
                            formik.touched.phone_number,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: formHelperFieldStyles,
                      }}
                    />
                  </div>

                  <div>
                    <p>Tanggal Lahir</p>
                    <DatePickerInput
                      value={formik.values.date_of_birth}
                      onChange={(date) => {
                        formik.setFieldValue("date_of_birth", date);
                      }}
                      onBlur={() =>
                        formik.setFieldTouched("date_of_birth", true)
                      }
                      error={
                        formik.touched.date_of_birth &&
                        Boolean(formik.errors.date_of_birth)
                      }
                      helperText={
                        formik.touched.date_of_birth &&
                        formik.errors.date_of_birth
                      }
                      placeholder="DD/MM/YYYY"
                    />
                  </div>

                  <div>
                    <p>Jenis Kelamin</p>
                    <FormControl
                      error={
                        formik.touched.gender && Boolean(formik.errors.gender)
                      }
                    >
                      <RadioGroup
                        row
                        value={formik.values.gender}
                        onChange={(e) =>
                          formik.setFieldValue("gender", Number(e.target.value))
                        }
                        onBlur={formik.handleBlur}
                        name="gender"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontFamily: '"Montserrat", sans-serif',
                            fontSize: "14px",
                          },
                        }}
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label="Laki-laki"
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label="Perempuan"
                        />
                      </RadioGroup>
                      {formik.touched.gender && formik.errors.gender && (
                        <FormHelperText sx={formHelperFieldStyles}>
                          {formik.errors.gender}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </div>

                  <div>
                    <p>Berat Badan (kg)</p>
                    <TextField
                      fullWidth
                      placeholder="Masukkan berat badan"
                      value={formik.values.weight}
                      onChange={(e) => {
                        if (!isNaN(Number(e.target.value)))
                          formik.setFieldValue(
                            "weight",
                            Number(e.target.value),
                          );
                      }}
                      onBlur={formik.handleBlur}
                      name="weight"
                      helperText={formik.touched.weight && formik.errors.weight}
                      error={
                        formik.touched.weight && Boolean(formik.errors.weight)
                      }
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.weight && formik.touched.weight,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: formHelperFieldStyles,
                      }}
                    />
                  </div>

                  <div>
                    <p>Tinggi Badan (cm)</p>
                    <TextField
                      fullWidth
                      placeholder="Masukkan tinggi badan"
                      value={formik.values.height}
                      onChange={(e) => {
                        if (!isNaN(Number(e.target.value)))
                          formik.setFieldValue(
                            "height",
                            Number(e.target.value),
                          );
                      }}
                      onBlur={formik.handleBlur}
                      name="height"
                      helperText={formik.touched.height && formik.errors.height}
                      error={
                        formik.touched.height && Boolean(formik.errors.height)
                      }
                      InputProps={{
                        sx: getFieldStyles(
                          formik.errors.height && formik.touched.height,
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: formHelperFieldStyles,
                      }}
                    />
                  </div>

                  {errorMessage && (
                    <div className="w-full flex justify-end mt-2">
                      <div className="flex items-center gap-2 bg-red-100 py-1.5 px-2.5 rounded-md">
                        <IoInformationCircleOutline
                          className="text-error shrink-0"
                          size={12}
                        />
                        <p className="text-sm text-red-700">{errorMessage}</p>
                      </div>
                    </div>
                  )}

                  <div className="fixed md:hidden bottom-0 left-0 right-0 bg-white rounded-t-3xl z-[99] max-h-[70vh] flex flex-col shadow-2xl">
                    <div className="w-full rounded-t-3xl flex items-center gap-2 px-3 py-4 text-base">
                      <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="h-[40px] border border-secondary flex items-center justify-center gap-2 bg-white text-secondary w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
                      >
                        Batal
                      </button>
                      <button
                        type="submit"
                        disabled={isPending || !formik.isValid}
                        className="h-[40px] border border-secondary flex items-center justify-center bg-secondary text-white w-full tracking-wide font-medium rounded-lg focus:outline-none focus:shadow-outline hover:bg-secondaryHover transform duration-200 hover:scale-100 disabled:bg-slate-400 disabled:cursor-disabled"
                      >
                        Simpan
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HOC(ChangeProfile, "required-auth");
